import {
  FANCY_CONTAINER_RADIUS_PX,
  FANCY_CONTAINER_SIZE_PX,
  IconContainerVariant,
  IconFancyContainer,
  IconFancyContainerSize,
} from '@components/icon-fancy-container/icon-fancy-container';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { BaseIcon } from '@components/icons/base';
import { IconBranch } from '@components/icons/branch';
import { IconPlaceholder } from '@components/icons/placeholder';
import { IconTrigger } from '@components/icons/trigger';
import { SquareImage } from '@components/square-image';

import { ReviewFindingTarget, ReviewTargetKind } from 'api/journey-reviews/requests';
import { ACTION_TYPE } from 'api/types';
import { actionConfig } from 'ui/actions/action-config';

import { JourneyNode } from '../node-types/types';

const CONTAINER_SIZE: IconFancyContainerSize = 'md';

export type FindingIcon =
  | { kind: 'image'; src: string }
  | { kind: 'icon'; Icon: typeof BaseIcon; variant: IconContainerVariant };

const KIND_ICONS: Record<ReviewTargetKind, FindingIcon> = {
  action: { kind: 'icon', Icon: IconPlaceholder, variant: 'gray' },
  branch: { kind: 'icon', Icon: IconBranch, variant: 'teal' },
  trigger: { kind: 'icon', Icon: IconTrigger, variant: 'blue' },
  field: { kind: 'icon', Icon: IconAttributeJourney, variant: 'blue' },
};

export const resolveFindingIcon = (
  target: ReviewFindingTarget,
  node: JourneyNode | undefined,
): FindingIcon => {
  const thumbnail = node?.data?.integration?.thumbnail;
  if (thumbnail) {
    return { kind: 'image', src: thumbnail };
  }

  const config = node ? actionConfig[node.type as ACTION_TYPE] : undefined;
  if (config) {
    return { kind: 'icon', Icon: config.icon, variant: config.variant };
  }

  return KIND_ICONS[target.kind];
};

export const FindingTargetIcon = ({
  target,
  node,
}: {
  target: ReviewFindingTarget;
  // Resolved by the list, not here: subscribing per card means one canvas
  // interaction re-renders every card and rescans the whole node array.
  node: JourneyNode | undefined;
}) => {
  const icon = resolveFindingIcon(target, node);

  if (icon.kind === 'image') {
    return (
      <SquareImage
        src={icon.src}
        size={FANCY_CONTAINER_SIZE_PX[CONTAINER_SIZE]}
        borderRadius={FANCY_CONTAINER_RADIUS_PX}
        alt=""
      />
    );
  }

  return (
    <IconFancyContainer
      size={CONTAINER_SIZE}
      variant={icon.variant}
      icon={<icon.Icon size={16} />}
    />
  );
};
